import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { checkDeletionGuard } from '../services/accountDeletion';

export function useWatchlist() {
  const { token, reportDeletion } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const request = useRef(null);
  const refresh = useCallback(async () => {
    if (!token) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/usernames`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      await checkDeletionGuard(response, reportDeletion, controller.signal);
      if (!response.ok) throw new Error('We could not load your watchlist. Please try again.');
      const data = await response.json();
      if (!Array.isArray(data) || data.some(item => !item || typeof item.username !== 'string')) {
        throw new Error('We could not read your watchlist. Please try again.');
      }
      if (!controller.signal.aborted) {
        setItems(data); setError(''); setUpdatedAt(new Date());
      }
    } catch (err) {
      if (!controller.signal.aborted) setError(err.message || 'We could not load your watchlist.');
    } finally {
      if (!controller.signal.aborted) setRefreshing(false);
    }
  }, [token, reportDeletion]);
  useEffect(() => {
    refresh();
    return () => request.current?.abort();
  }, [refresh]);
  return { items, setItems, error, setError, refreshing, updatedAt, refresh };
}
